import {
    ICreateHubAttendeeDTO,
    ICreateHubMenuLinkDTO,
    ICreateHubOrganizationDTO,
    ICreateHubPageDTO,
    IEventHubDTO,
    IEventHubListDTO,
    IEventHubRequestDTO,
    IEventHubsDAO,
    IEventHubVersionsDTO,
    IGetHubAttendeesRequestParams,
    IHubAttendeeDTO,
    IHubAttendeesListDTO,
    IHubConfigurationSettingsDTO,
    IHubMenuLinkDTO,
    IHubOrganizationDTO,
    IHubPageDTO,
    IHubPageDetailsDTO,
    THubConfigurationDTO,
    IUpdateEventHubSubdomainDTO,
    IUpdateHubPageDTO
} from '../event-hubs.dao';
import * as IoC from 'typescript-ioc';
import { IApiClient, RequestContentType } from '../../infrastructure/api-client';
import {
    CREATE_HUB_ATTENDEE_URL,
    DELETE_HUB_ORGANIZATION_URL,
    CREATE_HUB_ORGANIZATION_URL,
    EVENT_HUB_CREATE_URL,
    EVENT_HUB_CUSTOM_HTML_UPLOAD_URL,
    EVENT_HUB_PAGE_DETAILS_URL,
    EVENT_HUB_PAGE_UPDATE_URL,
    EVENT_HUB_MENU_LINK_CREATE_URL,
    EVENT_HUB_MENU_LINK_UPDATE_URL,
    EVENT_HUB_MENU_ITEMS_LIST_URL,
    EVENT_HUB_MENU_ITEMS_REORDER_URL,
    EVENT_HUB_PAGE_URL,
    EVENT_HUB_PAGES_LIST_URL,
    EVENT_HUB_SETTINGS_URL,
    EVENT_HUB_SUBDOMAIN_UPDATE_URL,
    EVENT_HUB_URL,
    EVENT_HUB_VERSIONS_URL,
    GET_HUB_ORGANIZATION_URL,
    EVENT_HUBS_LIST_URL,
    HUB_ATTENDEES_LIST_URL,
    HUB_ORGANIZATIONS_LIST_URL,
    UPDATE_HUB_ATTENDEE_URL,
    UPDATE_HUB_ORGANIZATION_URL
} from '../../constants/api/event-hubs-api.constants';
import { IAssetUrlDTO, IPaginationParams, replaceSortRequestParamsWithSortString } from '../shared.dto';
import { JobDtoType } from '../../domain/jobs';

export class EventHubsDAO extends IEventHubsDAO {
    constructor(@IoC.Inject private readonly apiClient: IApiClient) {
        super();
    }

    public async createEventHub(accountId: number, params: IEventHubRequestDTO): Promise<IEventHubDTO> {
        const response = await this.apiClient.post<IEventHubDTO>(EVENT_HUB_CREATE_URL, {
            accountId: accountId,
            ...params
        });
        return response.parsedResponse;
    }

    public async updateEventHub(accountId: number, eventHubId: number, params: IEventHubRequestDTO): Promise<IEventHubDTO> {
        const response = await this.apiClient.put<IEventHubDTO>(EVENT_HUB_URL, {
            accountId: accountId,
            eventHubId: eventHubId,
            ...params
        });
        return response.parsedResponse;
    }

    public async deleteEventHub(accountId: number, eventHubId: number): Promise<void> {
        await this.apiClient.delete<IEventHubDTO>(EVENT_HUB_URL, {
            accountId: accountId,
            eventHubId: eventHubId,
        });
    }

    public async getEventHubs(accountId: number, paginationParams?: IPaginationParams): Promise<IEventHubListDTO> {
        const response = await this.apiClient.get<IEventHubListDTO>(EVENT_HUBS_LIST_URL, {
            accountId: accountId,
            ...paginationParams
        });
        return response.parsedResponse;
    }

    public async getEventHub(accountId: number, eventHubId: number): Promise<IEventHubDTO> {
        const response = await this.apiClient.get<IEventHubDTO>(EVENT_HUB_URL, {
            accountId: accountId,
            eventHubId: eventHubId
        });
        return response.parsedResponse;
    }

    public async updateEventHubSubdomain(accountId: number, eventHubId: number, payload: IUpdateEventHubSubdomainDTO): Promise<IEventHubDTO> {
        const response = await this.apiClient.put<IEventHubDTO>(EVENT_HUB_SUBDOMAIN_UPDATE_URL, {
            accountId: accountId,
            eventHubId: eventHubId,
            ...payload
        });
        return response.parsedResponse;
    }

    public async createHubOrganization(accountId: number, eventHubId: number, params: ICreateHubOrganizationDTO): Promise<IHubOrganizationDTO> {
        const response = await this.apiClient.post<IHubOrganizationDTO>(CREATE_HUB_ORGANIZATION_URL, {
            accountId: accountId,
            eventHubId: eventHubId,
            ...params
        });
        return response.parsedResponse;
    }

    public async updateHubOrganization(
        accountId: number,
        eventHubId: number,
        organizationId: number,
        params: ICreateHubOrganizationDTO
    ): Promise<IHubOrganizationDTO> {
        const response = await this.apiClient.put<IHubOrganizationDTO>(UPDATE_HUB_ORGANIZATION_URL, {
            accountId: accountId,
            eventHubId: eventHubId,
            organizationId: organizationId,
            ...params
        });
        return response.parsedResponse;
    }

    public async getHubOrganization(accountId: number, eventHubId: number, organizationId: number): Promise<IHubOrganizationDTO> {
        const response = await this.apiClient.get<IHubOrganizationDTO>(GET_HUB_ORGANIZATION_URL, {
            accountId: accountId,
            eventHubId: eventHubId,
            organizationId: organizationId,
        });
        return response.parsedResponse;
    }

    public async deleteHubOrganization(accountId: number, eventHubId: number, organizationId: number): Promise<JobDtoType | void> {
        const response = await this.apiClient.delete<JobDtoType>(DELETE_HUB_ORGANIZATION_URL, {
            accountId: accountId,
            eventHubId: eventHubId,
            organizationId: organizationId,
        });
        return response.parsedResponse;
    }

    public async getHubOrganizations(accountId: number, eventHubId: number): Promise<IHubOrganizationDTO[]> {
        const response = await this.apiClient.get<IHubOrganizationDTO[]>(HUB_ORGANIZATIONS_LIST_URL, {
            accountId: accountId,
            eventHubId: eventHubId
        });
        return response.parsedResponse;
    }

    public async getHubAttendees(params: IGetHubAttendeesRequestParams): Promise<IHubAttendeesListDTO> {
        const { accountId, eventHubId, sortField, sortOrder, ...otherParams } = params;
        const sortParams = sortField && sortOrder
            ? replaceSortRequestParamsWithSortString({ sortField: sortField, sortOrder: sortOrder, ...otherParams })
            : otherParams;
        const response = await this.apiClient.get<IHubAttendeesListDTO>(HUB_ATTENDEES_LIST_URL, {
            accountId: accountId,
            eventHubId: eventHubId,
            ...sortParams
        });
        return response.parsedResponse;
    }

    public async createHubAttendee(accountId: number, eventHubId: number, params: ICreateHubAttendeeDTO): Promise<IHubAttendeeDTO> {
        const response = await this.apiClient.post<IHubAttendeeDTO>(CREATE_HUB_ATTENDEE_URL, {
            accountId: accountId,
            eventHubId: eventHubId,
            ...params
        });
        return response.parsedResponse;
    }

    public async updateHubAttendee(accountId: number, eventHubId: number, attendeeId: number, params: ICreateHubAttendeeDTO): Promise<IHubAttendeeDTO> {
        const response = await this.apiClient.put<IHubAttendeeDTO>(UPDATE_HUB_ATTENDEE_URL, {
            accountId: accountId,
            eventHubId: eventHubId,
            attendeeId: attendeeId,
            ...params
        });
        return response.parsedResponse;
    }

    /**
     * Same resource path as update; HTTP DELETE. No client-side permission wrapper — same pattern as
     * createHubAttendee / updateHubAttendee / deleteEventHub (server authorizes).
     */
    public async deleteHubAttendee(accountId: number, eventHubId: number, attendeeId: number): Promise<void> {
        await this.apiClient.delete<void>(UPDATE_HUB_ATTENDEE_URL, {
            accountId: accountId,
            eventHubId: eventHubId,
            attendeeId: attendeeId,
        });
    }

    public async getEventHubConfigs(accountId: number, eventHubId: number): Promise<IHubConfigurationSettingsDTO> {
        const response = await this.apiClient.get<IHubConfigurationSettingsDTO>(EVENT_HUB_SETTINGS_URL, {
            accountId: accountId,
            eventHubId: eventHubId
        });
        return response.parsedResponse;
    }

    public async setEventHubConfig(params: THubConfigurationDTO | null): Promise<THubConfigurationDTO> {
        const response = await this.apiClient.put<THubConfigurationDTO>(EVENT_HUB_SETTINGS_URL, params || {});
        return response.parsedResponse;
    }

    public async getEventHubVersions(eventHubId: number): Promise<IEventHubVersionsDTO> {
        const response = await this.apiClient.get<IEventHubVersionsDTO>(EVENT_HUB_VERSIONS_URL, {
            eventHubId: eventHubId
        });
        return response.parsedResponse;
    }

    public async getHubPages(hubVersionId: number): Promise<IHubPageDTO[]> {
        const response = await this.apiClient.get<IHubPageDTO[]>(EVENT_HUB_PAGES_LIST_URL, {
            hubVersionId: hubVersionId
        });
        return response.parsedResponse;
    }

    public async getMenuItems(hubVersionId: number): Promise<(IHubPageDTO | IHubMenuLinkDTO)[]> {
        const response = await this.apiClient.get<(IHubPageDTO | IHubMenuLinkDTO)[]>(EVENT_HUB_MENU_ITEMS_LIST_URL, {
            hubVersionId: hubVersionId
        });
        return response.parsedResponse;
    }

    public async getHubPage(hubPageId: number): Promise<IHubPageDetailsDTO> {
        const response = await this.apiClient.get<IHubPageDetailsDTO>(EVENT_HUB_PAGE_DETAILS_URL, {
            hubPageId: hubPageId
        });
        return response.parsedResponse;
    }

    public async createHubPage(hubVersionId: number, params: ICreateHubPageDTO): Promise<IHubPageDetailsDTO> {
        const response = await this.apiClient.post<IHubPageDetailsDTO>(EVENT_HUB_PAGE_URL, {
            hubVersionId: hubVersionId,
            ...params
        });
        return response.parsedResponse;
    }

    public async updateHubPage(params: IUpdateHubPageDTO): Promise<IHubPageDetailsDTO> {
        const response = await this.apiClient.put<IHubPageDetailsDTO>(EVENT_HUB_PAGE_UPDATE_URL, {
            ...params
        });
        return response.parsedResponse;
    }

    public async createHubMenuLink(hubVersionId: number, params: ICreateHubMenuLinkDTO): Promise<IHubMenuLinkDTO> {
        const response = await this.apiClient.post<IHubMenuLinkDTO>(EVENT_HUB_MENU_LINK_CREATE_URL, {
            hubVersionId: hubVersionId,
            ...params
        });
        return response.parsedResponse;
    }

    public async updateHubMenuLink(hubMenuLinkId: number, params: ICreateHubMenuLinkDTO): Promise<IHubMenuLinkDTO> {
        const response = await this.apiClient.put<IHubMenuLinkDTO>(EVENT_HUB_MENU_LINK_UPDATE_URL, {
            hubMenuLinkId: hubMenuLinkId,
            ...params
        });
        return response.parsedResponse;
    }

    public async reorderMenuItems(hubVersionId: number, orderedMenuItemIds: number[]): Promise<(IHubPageDTO | IHubMenuLinkDTO)[]> {
        const response = await this.apiClient.put<(IHubPageDTO | IHubMenuLinkDTO)[]>(EVENT_HUB_MENU_ITEMS_REORDER_URL, {
            hubVersionId: hubVersionId,
            orderedMenuItemIds: orderedMenuItemIds
        });
        return response.parsedResponse;
    }

    public async uploadCustomHtmlZip(accountId: number, eventHubId: number, file: File): Promise<IAssetUrlDTO> {
        const urlResponse = await this.apiClient.get<IAssetUrlDTO>(EVENT_HUB_CUSTOM_HTML_UPLOAD_URL, {
            accountId: accountId,
            eventHubId: eventHubId
        });
        const { assetUrl, objectKey } = urlResponse.parsedResponse;
        await this.apiClient.put(assetUrl, file, { ContentType: RequestContentType.zip });
        return { assetUrl: assetUrl, objectKey: objectKey };
    }
}
